/**
 * Service for interacting with Auth0
 */
import auth0 from 'auth0-js';
import { v4 as uuidv4 } from 'uuid';

const auth = new auth0.WebAuth({
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  redirectUri: process.env.REACT_APP_AUTH0_CALLBACK_URL,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  responseType: 'token id_token',
  scope: 'openid email name',
});

// Create params object
const urlParams = new URLSearchParams(window.location.search);

// see if google analytics param exists and add to local storage
if (urlParams.has('_ga')) {
  localStorage.setItem('_ga', urlParams.get('_ga'));
}

// see if hubspot params exist and add to local storage
if (urlParams.has('__hsfp') && urlParams.has('__hstc') && urlParams.has('__hssc')) {
  localStorage.setItem('__hsfp', urlParams.get('__hsfp'));
  localStorage.setItem('__hstc', urlParams.get('__hstc'));
  localStorage.setItem('__hssc', urlParams.get('__hssc'));
}

/**
 * Manually build the auth() url
 */
const manualAuthorize = (sso = false) => {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientID = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_AUTH0_CALLBACK_URL;
  const state = uuidv4();
  const nonce = uuidv4();
  const responseType = 'token id_token';
  const scope = 'openid email name';
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
  const options = {
    responseType,
    nonce,
    state,
  };

  // create transaction
  auth.transactionManager.process(options);

  // build the base url
  let url = `https://${domain}/authorize?response_type=${responseType}&state=${state}&nonce=${nonce}&scope=${scope}&client_id=${clientID}&redirect_uri=${redirectUri}&audience=${audience}`;

  if (sso) {
    url = `${url}&connection=RAX-SSO`; // TODO: Should store it in env? `RAX-SSO-PRD`
  }

  // see if google analytics param exists and add
  if ('_ga' in localStorage) {
    const ga = localStorage.getItem('_ga');
    url = url.concat('&_ga=', ga);
  }

  // see if hubspot params exist and add
  if ('__hsfp' in localStorage && '__hstc' in localStorage && '__hssc' in localStorage) {
    const hsfp = localStorage.getItem('__hsfp');
    const hstc = localStorage.getItem('__hstc');
    const hssc = localStorage.getItem('__hssc');
    url = url.concat('&__hsfp=', hsfp, '&__hstc=', hstc, '&__hssc=', hssc);
  }

  // redirect
  window.location.assign(url);
};

/**
 * Signs up a AWS user, logs them in, and redirects them back to the AWS onboarding page
 */
const logInAWS = (data, awsToken) =>
  new Promise((resolve, reject) => {
    auth.signup(
      {
        connection: 'Username-Password-Authentication',
        email: data.username,
        password: data.password,
        username: data.username,
        user_metadata: {
          firstName: data.firstname,
          lastName: data.lastname,
          awsToken,
        },
      },
      (err) => {
        if (err) {
          return reject(err);
        }
        return resolve(
          auth.login({
            password: data.password,
            username: data.username,
            realm: 'Username-Password-Authentication',
            redirectUri: `${process.env.REACT_APP_DOMAIN}/aws_onboarding?token=${awsToken}`,
          }),
        );
      },
    );
  });

/**
 * Logs user out of auth0 session by calling auth0 logout method
 */
const logOut = () => {
  auth.logout({
    clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
    returnTo: `${process.env.REACT_APP_DOMAIN}/login`,
  });
};

/**
 * Parse the session information from the given URL and returns it
 *
 * @param {string} location - The current URL including the token
 * @returns {Promise} - Promise that resolves with the user's credentials
 *  if they can be parsed or rejects otherwise
 */
const parseCreds = (location) =>
  new Promise((resolve, reject) => {
    if (location && /access_token|id_token|error/.test(location.hash)) {
      auth.parseHash((error, authResult) => {
        if (!error && authResult && authResult.accessToken && authResult.idToken) {
          resolve(authResult);
        } else {
          reject(error);
        }
      });
    } else {
      reject();
    }
  });

/**
 * Tries to refresh the session.
 *
 * @returns {Promise} - Promise that resolves with the user's credentials
 *  if they can be renewed
 */
const checkSession = () =>
  new Promise((resolve, reject) => {
    auth.checkSession(
      {
        nonce: uuidv4(),
        state: uuidv4(),
      },
      (error, authResult) => {
        if (!error && authResult && authResult.accessToken && authResult.idToken) {
          resolve(authResult);
        } else {
          reject(error);
        }
      },
    );
  });

export default {
  manualAuthorize,
  checkSession,
  logOut,
  logInAWS,
  parseCreds,
};
